import React, { useState } from 'react';
import { 
  Download, 
  FileDown, 
  Calendar, 
  ChevronDown, 
  Check, 
  FileText, 
  BarChart, 
  Clipboard, 
  MapPin 
} from 'lucide-react';

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState('performance');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const handleGenerate = () => {
    setIsGenerating(true);
    // Simulate API call
    setTimeout(() => {
      setIsGenerating(false);
    }, 2000);
  };
  
  const reportTypes = [
    { id: 'performance', name: 'Performance Summary', icon: BarChart },
    { id: 'detailed', name: 'Detailed Scorecard', icon: Clipboard },
    { id: 'regional', name: 'Regional Comparison', icon: MapPin },
    { id: 'custom', name: 'Custom Report', icon: FileText },
  ];
  
  const exportFormats = [
    { id: 'pdf', name: 'PDF Document' },
    { id: 'excel', name: 'Excel Spreadsheet' },
    { id: 'csv', name: 'CSV File' },
  ];
  
  const timePeriods = [
    { id: 'day', name: 'Today' },
    { id: 'week', name: 'This Week' },
    { id: 'month', name: 'This Month' },
    { id: 'quarter', name: 'This Quarter' },
    { id: 'year', name: 'This Year' },
    { id: 'custom', name: 'Custom Range' },
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Scorecard Reports</h2>
        <p className="text-sm text-gray-500 mt-1">Generate and export performance reports</p>
      </div>
      
      {/* Report Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Type */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Report Type</h3>
          <div className="space-y-3">
            {reportTypes.map((type) => (
              <div 
                key={type.id}
                onClick={() => setSelectedReport(type.id)}
                className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedReport === type.id 
                    ? 'bg-blue-50 border-blue-200 border' 
                    : 'hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <div className={`p-2 rounded-lg ${
                  selectedReport === type.id ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <type.icon className={`h-5 w-5 ${
                    selectedReport === type.id ? 'text-blue-600' : 'text-gray-500'
                  }`} />
                </div>
                <span className={`ml-3 text-sm font-medium ${
                  selectedReport === type.id ? 'text-blue-700' : 'text-gray-700'
                }`}>
                  {type.name}
                </span>
                {selectedReport === type.id && (
                  <Check className="ml-auto h-5 w-5 text-blue-600" />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Report Options */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Report Options</h3>
          
          <div className="space-y-4">
            {/* Time Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Period
              </label>
              <div className="relative">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {timePeriods.map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
            
            {/* Export Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Export Format
              </label>
              <div className="space-y-2">
                {exportFormats.map((format) => (
                  <div key={format.id} className="flex items-center">
                    <input
                      id={`format-${format.id}`}
                      name="export-format"
                      type="radio"
                      checked={selectedFormat === format.id}
                      onChange={() => setSelectedFormat(format.id)}
                      className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor={`format-${format.id}`} className="ml-3 block text-sm font-medium text-gray-700">
                      {format.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Additional Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Options
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="include-charts"
                    name="include-charts"
                    type="checkbox"
                    defaultChecked={true}
                    className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="include-charts" className="ml-3 block text-sm text-gray-700">
                    Include charts and graphs
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="include-comparison"
                    name="include-comparison"
                    type="checkbox"
                    defaultChecked={true}
                    className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="include-comparison" className="ml-3 block text-sm text-gray-700">
                    Include previous period comparison
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    id="include-comments"
                    name="include-comments"
                    type="checkbox"
                    defaultChecked={false}
                    className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="include-comments" className="ml-3 block text-sm text-gray-700">
                    Include manager comments
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Preview & Generate */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Preview & Generate</h3>
          
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-6 min-h-[200px] flex items-center justify-center">
            <div className="text-center">
              <FileText className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">
                {selectedReport === 'performance' && 'Performance Summary Report'}
                {selectedReport === 'detailed' && 'Detailed Scorecard Report'}
                {selectedReport === 'regional' && 'Regional Comparison Report'}
                {selectedReport === 'custom' && 'Custom Report'}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Format: {selectedFormat.toUpperCase()} • Period: {
                  timePeriods.find(p => p.id === selectedPeriod).name
                }
              </p>
            </div>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
              ${isGenerating ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Report...
              </>
            ) : (
              <>
                <Download className="mr-2 h-5 w-5" />
                {selectedFormat === 'pdf' && 'Generate PDF Report'}
                {selectedFormat === 'excel' && 'Generate Excel Report'}
                {selectedFormat === 'csv' && 'Generate CSV Report'}
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Recent Reports */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Recent Reports</h2>
          <p className="text-sm text-gray-500 mt-1">Previously generated reports</p>
        </div>
        
        <div className="divide-y divide-gray-100">
          {[
            { 
              name: 'June 2024 Performance Summary', 
              type: 'PDF', 
              size: '2.4 MB',
              date: 'June 5, 2024', 
              user: 'Ahmed Al-Qahtani' 
            },
            { 
              name: 'Q2 2024 Regional Comparison', 
              type: 'Excel', 
              size: '4.2 MB',
              date: 'June 2, 2024', 
              user: 'Sarah Al-Harbi' 
            },
            { 
              name: 'May 2024 Detailed Scorecard', 
              type: 'PDF', 
              size: '3.8 MB',
              date: 'May 31, 2024', 
              user: 'Khalid Al-Otaibi' 
            },
            { 
              name: 'Store Performance Trends Q1-Q2 2024', 
              type: 'Excel', 
              size: '5.1 MB',
              date: 'May 15, 2024',
              user: 'Mohammed Al-Shehri' 
            },
            { 
              name: 'April 2024 Performance Summary', 
              type: 'PDF', 
              size: '2.3 MB',
              date: 'May 3, 2024', 
              user: 'Omar Al-Subaie' 
            }
          ].map((report, index) => (
            <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  {report.type === 'PDF' ? (
                    <div className="p-2 bg-red-100 rounded-lg">
                      <FileText className="h-5 w-5 text-red-600" />
                    </div>
                  ) : (
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{report.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {report.type} • {report.size} • {report.date}
                    </p>
                  </div>
                </div>
                <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors">
                  <FileDown className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports; 